import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { InputText } from '../../../../shared/components/core-ui/input-text/input-text.component';
import { TeamUiModel } from '../../../../shared/ui-models/team.ui-model';
import { AccessControlFilterConfigModel } from '../../../../shared/models/team.model';
import { MarketingChannel } from '../../../../shared/interfaces/attribution.interface';
import { AreaUiModel } from '../../../../shared/ui-models/area.ui-model';
import { getAllAreas } from '../../../../shared/services/data/lookups.data';
import { isErrorInfo, ErrorInfo } from '../../../../shared/interfaces/error-info.interface';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';

interface Option {
  value: string;
  label: string;
}

/** `marketingChannel` reads better as "Marketing channel" above a picker. */
const titleCase = (value: string): string =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c: string) => c.toUpperCase());

export interface TeamFormValue {
  name: string;
  accessControl: { module: string; enforcedFilters: { key: string; value: string[] }[] }[];
}

interface TeamFormModalProps {
  show: boolean;
  team?: TeamUiModel;
  /** The allow-list the server validates against. Pickers are built from it. */
  filterConfig: AccessControlFilterConfigModel;
  onClose: () => void;
  onSubmit: (value: TeamFormValue) => void;
}

export const TeamFormModal: React.FC<TeamFormModalProps> = ({
  show,
  team,
  filterConfig,
  onClose,
  onSubmit
}) => {
  const isEdit = Boolean(team);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [areas, setAreas] = useState<AreaUiModel[]>([]);

  /**
   * Areas are fetched, not listed in the service's allow-list.
   *
   * `access-control-filters.config.ts` deliberately leaves `allowedValues`
   * undefined for `areaId` and `marketingChannel`: areas live in
   * server.service.property and are created continuously, and the channel
   * vocabulary is owned by the lead service. Pinning either into the team
   * service would mean a deploy every time one changes. That is the right call
   * there — but it makes it this form's job to find the values, which it was
   * not doing, so both rendered as an empty dropdown saying "No options".
   */
  useEffect(() => {
    if (!show) return;
    getAllAreas('TeamFormModal').then((response: AreaUiModel[] | ErrorInfo) => {
      if (!isErrorInfo(response)) setAreas(response);
    });
  }, [show]);

  const leadFilters = filterConfig.leads ?? {};
  const filterKeys = Object.keys(leadFilters);

  const areaOptions: Option[] = useMemo(
    () =>
      areas
        .filter((area: AreaUiModel) => Boolean(area.id))
        .map((area: AreaUiModel) => ({ value: area.id as string, label: area.name })),
    [areas]
  );

  const channelOptions: Option[] = useMemo(
    () => Object.values(MarketingChannel).map((value: string) => ({ value, label: value })),
    []
  );

  /**
   * Where a key's values come from, in order: the service's own allow-list,
   * then a client-side source for the two keys the service deliberately leaves
   * open, then nothing.
   *
   * A key with no source is rendered as a free-text field rather than a dead
   * dropdown — the lead service validates the value when the grant is used, and
   * an empty picker with no way to type is simply a wall.
   */
  const optionsFor = (key: string): { options: Option[]; creatable: boolean } => {
    const allowed = leadFilters[key]?.allowedValues;
    if (allowed?.length) {
      return {
        options: allowed.map((value: string) => ({ value, label: value })),
        creatable: false
      };
    }
    if (key === 'areaId') return { options: areaOptions, creatable: false };
    if (key === 'marketingChannel') return { options: channelOptions, creatable: false };
    return { options: [], creatable: true };
  };

  /** A stored value whose option no longer resolves still has to render. */
  const valuesFor = (key: string, options: Option[]): Option[] =>
    (selected[key] ?? []).map(
      (value: string) =>
        options.find((option: Option) => option.value === value) ?? { value, label: value }
    );

  useEffect(() => {
    setName(team?.name ?? '');
    const grant = team?.grants.find((item) => item.module === 'leads');
    const next: Record<string, string[]> = {};
    (grant?.filters ?? []).forEach((filter) => {
      next[filter.key] = filter.values;
    });
    setSelected(next);
  }, [team, show]);

  const setValues = (key: string, values: string[]) => {
    const updated = { ...selected };
    if (values.length) updated[key] = values;
    else delete updated[key];
    setSelected(updated);
  };

  const hasAnyFilter = Object.keys(selected).length > 0;

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[85vh] lg:w-[620px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          {isEdit ? 'Edit team' : 'Create team'}
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-4">
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={name.trim().length === 0}
            tracking={{
              event: 'record_saved',
              entity: 'team',
              action: isEdit ? 'update' : 'create',
              outcome: 'success'
            }}
            onClick={() =>
              onSubmit({
                name: name.trim(),
                accessControl: [
                  {
                    module: 'leads',
                    enforcedFilters: Object.keys(selected).map((key: string) => ({
                      key,
                      value: selected[key]
                    }))
                  }
                ]
              })
            }
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      }
    >
      {/* `w-full` is load-bearing: ui/Model's body is `flex flex-col
          items-start`, so a child that does not claim the width shrinks to its
          content — which collapsed every react-select in here to about 85px.
          Every other user of this modal wraps its children the same way. */}
      <div className="flex w-full flex-col gap-4">
        <div>
          <label className={LABEL}>Name</label>
          <InputText
            data={{ value: name, placeholder: 'e.g. Acquisition — Cairo' }}
            config={{ required: true }}
            eventHandlers={{ onChangeHandler: (value: string) => setName(value) }}
          />
        </div>

        <div>
          <h3 className="font-gilroy-bold text-sm text-neutral-500">Lead access</h3>
          <p className="mb-3 text-xs text-neutral-400">
            Leave everything empty and the team gets an unconditional org-wide browse of leads. Pick
            values and the browse is narrowed to them. This grant widens what managers see beyond
            their own downline — it never narrows an agent&apos;s own book.
          </p>

          {filterKeys.length === 0 ? (
            <p className="text-xs text-neutral-400">No filter keys configured.</p>
          ) : (
            filterKeys.map((key: string) => {
              const { options, creatable } = optionsFor(key);
              const common = {
                isMulti: true as const,
                menuPortalTarget: document.body,
                menuPosition: 'fixed' as const,
                styles: selectMenuPortalStyles,
                classNamePrefix: 'react-select',
                options,
                value: valuesFor(key, options),
                onChange: (value: ValueType<Option, true>) =>
                  setValues(
                    key,
                    ((value as Option[]) || []).map((option) => option.value)
                  )
              };
              return (
                <div key={key} className="mb-3">
                  <label className={LABEL}>
                    {titleCase(key)}
                    <span className="ml-2 font-normal text-neutral-300">
                      {leadFilters[key].description}
                    </span>
                  </label>
                  {creatable ? (
                    <CreatableSelect
                      {...common}
                      placeholder="Any — type a value and press enter"
                      formatCreateLabel={(input: string) => `Use "${input}"`}
                      noOptionsMessage={() => 'Type a value and press enter'}
                    />
                  ) : (
                    <Select
                      {...common}
                      placeholder="Any"
                      noOptionsMessage={() =>
                        key === 'areaId' ? 'No areas loaded' : 'Nothing to choose from'
                      }
                    />
                  )}
                </div>
              );
            })
          )}

          {!hasAnyFilter ? (
            <p className="rounded-xl bg-creams-300 px-3 py-2 text-xs text-creams-900">
              This team will be able to browse <strong>every</strong> lead.
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
